import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { durationOptions, TaskPriority, TasksPriorityArr, TaskStatus, TaskStatusDispaly, UserTypeId } from "../../common/const";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { Add, Close, WatchLater } from "@mui/icons-material";
import { CreateTask, getTasks, UpdateTask } from "../../api/satactApi";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";

const CoachTask = ({
    testType,
    fromMentor,
    studentId,
    title,
    currentService
}) => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [tasks, setTasks] = useState([]);
    const [taskOpen, setTaskOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        name: '',
        due_date: '',
        priority: '',
        notes: '',
        resources: '',
        duration: durationOptions[0].value
    });

    const getTaskData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getTasks(testType, user?.student_id || studentId);
            setTasks(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id || studentId) {
            getTaskData();
        }
    }, [testType, user, fromMentor]);

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                if (!value) return 'Title is required';
                return '';
            case 'due_date':
                if (!value) return 'Due Date is required';
                return '';
            case 'priority':
                if (!value) return 'Priority is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files.length > 0) {
            const file = files;

            // Validate file size immediately
            const error = validateField(name, '', file); // Pass empty string for value, file for validation

            setFormData((prev) => ({
                ...prev,
                [name]: file,
            }));

            // Set or clear error
            if (error) {
                setErrors(prev => ({ ...prev, [name]: error }));
            } else {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const data = new FormData();
            data.append('title', formData.name);
            data.append('description', formData.notes);
            data.append('priority', formData.priority);
            data.append('due_date', new Date(formData.due_date).toISOString());
            data.append('created_by', user?.user_id);
            data.append('assigned_by', user?.user_id);
            data.append('student_id', studentId);
            data.append('status', TaskStatus.PENDING);
            data.append('type', testType);
            data.append('estimated_minutes', formData.duration);
            data.append('mentor_profile_id', user?.mentor?.mentor_profile_id);
            data.append('service_offers', currentService);
            if (formData.resources) {
                data.append('resource', formData.resources[0]);
            }
            const response = await CreateTask(data);
            if (response.success) {
                showNotification(response.message, 'success');
                setFormData({
                    name: '',
                    due_date: '',
                    priority: '',
                    notes: '',
                    resources: '',
                    duration: durationOptions[0].value
                });
                setTaskOpen(false);
                dispatch(setLoading({
                    loading: false
                }));
                getTaskData();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={taskOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Create New Task</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setTaskOpen(false);
                                    setErrors({});
                                    setFormData({
                                        name: '',
                                        due_date: '',
                                        priority: '',
                                        notes: '',
                                        resources: '',
                                        duration: durationOptions[0].value
                                    });
                                }}
                            />
                        </Box>
                        <Box width="80%" mb={0.5}>
                            <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Enter the task details</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} sm={12}>
                            <CommonFormField
                                label="Task Title"
                                type="email"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={errors.name}
                                placeholder="Enter Task Title"
                            />
                        </Grid>
                        <Grid item md={12} sm={12}>
                            <CommonSelect
                                label="Priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleInputChange}
                                placeholder="Select Priority"
                                error={errors.priority}
                                options={TasksPriorityArr}
                            />
                        </Grid>
                        <Grid item md={12} sm={12}>
                            <CommonSelect
                                label="Duration"
                                name="duration"
                                value={formData.duration}
                                onChange={handleInputChange}
                                placeholder="Select Duration"
                                options={durationOptions}
                            />
                        </Grid>
                        <Grid item md={12} sm={12}>
                            <CommonFormField
                                label="Due Date"
                                type="date"
                                name="due_date"
                                value={formData.due_date}
                                onChange={handleInputChange}
                                error={errors.due_date}
                                placeholder="Select Due Date"
                            />
                        </Grid>
                        <Grid item md={12} sm={12}>
                            <CommonFormField
                                label="Description"
                                type="email"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                placeholder="Describe what success"
                                multiline
                            />
                        </Grid>
                        <Grid item md={12} sm={12}>
                            <CommonFormField
                                label="Link Resource"
                                type="file"
                                name="resources"
                                value={formData.resources?.[0]?.name}
                                onChange={handleInputChange}
                                placeholder="PDF"
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label="Create"
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={() => {
                            handleSubmit();
                        }}
                    />
                )}
            />
            <CommonCard
                paddingBottom="0px"
                padding="0px"
                borderRadius={1}
                marginTop={fromMentor && "0px"}
                border={fromMentor ? `1px solid #0000001A` : 'none'}
                headerContent={
                    <Fragment>
                        <Box p={2}>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box>
                                    <Typography fontWeight={600}>{fromMentor ? title ? title : 'Assigned Tasks' : `Coach's Priority Tasks`}</Typography>
                                    {!fromMentor && (
                                        <Typography fontSize={14} color="background.dark">Focus areas recommended by your coach to improve your score.</Typography>
                                    )}
                                </Box>
                                {title && (
                                    <CommonButton
                                        label="Assign Task"
                                        height="35px"
                                        width="140px"
                                        marginBottom="0px"
                                        marginTop="0px"
                                        startIcon={<Add />}
                                        handleClick={() => setTaskOpen(true)}
                                    />
                                )}
                            </Box>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box
                        sx={{
                            height: tasks.length > 0 ? fromMentor ? '21vh' : '22vh' : '8vh',
                            overflow: 'auto'
                        }}
                    >
                        {tasks.length > 0 ? (
                            <Grid container spacing={1} p={2}>
                                {tasks.map((a, index) => (
                                    <Grid item xs={fromMentor ? 12 : 6} key={index}>
                                        <CommonCard
                                            border={`1px solid #0000001A`}
                                            borderRadius={1}
                                            paddingBottom='0px'
                                            marginTop="0px"
                                            padding="0px"
                                            content={
                                                <Fragment>
                                                    <Box p="8px">
                                                        <Box
                                                            sx={{
                                                                background: a?.priority === 'MEDIUM' ? theme.palette.secondary.main : a?.priority === 'HIGH' ? 'red' : '#01875F',
                                                                textAlign: 'center',
                                                                height: 25,
                                                                borderRadius: 1,
                                                                py: 0.3,
                                                                px: 1,
                                                                display: 'inline-block'
                                                            }}
                                                        >
                                                            <Typography fontSize={13} color={a?.priority === 'MEDIUM' ? theme.palette.primary.main : a?.priority === 'HIGH' ? 'white' : 'white'} textAlign="center">{TaskPriority[a?.priority]}</Typography>
                                                        </Box>
                                                        <Typography mt={1} fontWeight={600} fontSize={15}>{a?.title}</Typography>
                                                        <Typography fontSize={13} color="background.dark" width="80%">{a?.description}</Typography>
                                                    </Box>
                                                    <Divider />
                                                    <Box display="flex" justifyContent="space-between" p={2} alignItems="center">
                                                        <Box display="flex" gap={0.5} alignItems="center">
                                                            <WatchLater sx={{ color: theme.palette.background.dark, fontSize: 18 }} />
                                                            <Typography fontSize={14} fontWeight={600}>{a?.estimated_minutes || 0} <span style={{ fontWeight: 'normal', fontSize: 12, color: theme.palette.background.dark }}>Mins</span></Typography>
                                                        </Box>
                                                        {!fromMentor && user?.user_type_id === UserTypeId.Student && (a?.status === TaskStatus.PENDING || a?.status === TaskStatus.IN_PROGRESS) ? (
                                                            <CommonButton
                                                                label={a?.status === TaskStatus.PENDING ? "Start" : "Complete"}
                                                                fontWeight={600}
                                                                width={a?.status === TaskStatus.PENDING ? "50px" : "90px"}
                                                                height="30px"
                                                                marginBottom="0px"
                                                                handleClick={async () => {
                                                                    try {
                                                                        dispatch(setLoading({
                                                                            loading: true
                                                                        }));
                                                                        const response = await UpdateTask(a.task_id, {
                                                                            "status": a?.status === TaskStatus?.PENDING ? TaskStatus.IN_PROGRESS : TaskStatus.COMPLETED,
                                                                            "completed_at": new Date().toISOString()
                                                                        });
                                                                        if (response?.success) {
                                                                            showNotification(response.message, 'success');
                                                                            getTaskData();
                                                                        }
                                                                    } catch (err) {
                                                                        const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                                        showNotification(errorMessage, 'error');
                                                                        dispatch(setLoading({
                                                                            loading: false
                                                                        }));
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    background: '#01875F',
                                                                    padding: '3px 10px',
                                                                    borderRadius: 1,
                                                                }}
                                                            >
                                                                <Typography fontSize={13} color={'white'} textAlign="center">{TaskStatusDispaly[a?.status]}</Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Fragment>
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                <Typography color={theme.palette.background.dark}>No Task Found</Typography>
                            </Box>
                        )}
                    </Box>
                }
            />
        </Fragment>
    )
}

export default CoachTask;